import { useState, useEffect } from "react"
import { shellControlClass } from "@/components/layout/shellControl";
import { differenceInCalendarDays, format, parse, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowLeft01Icon, Calendar01Icon } from "@hugeicons/core-free-icons"

/**
 * DateDropdown: preset list, plus a custom range panel (presets rail, two
 * months, footer with the picked span and Apply as the one orange action).
 */

const periodOptions = [
  { value: "1d", label: "Last 24 hours", group: "Recent" },
  { value: "7d", label: "Last 7 days", group: "Recent" },
  { value: "30d", label: "Last 30 days", group: "Recent" },
  { value: "90d", label: "Last 90 days", group: "Extended" },
  { value: "mtd", label: "Month to date", group: "Current Period" },
  { value: "ytd", label: "Year to date", group: "Current Period" },
  { value: "12m", label: "Last 12 months", group: "Extended" },
];

// Organized preset groups for better UX
const presetGroups = [
  {
    label: "Quick",
    presets: [
      { 
        label: "Today", 
        getValue: () => {
          const today = new Date();
          return { from: today, to: today };
        }
      },
      { 
        label: "Yesterday", 
        getValue: () => {
          const yesterday = subDays(new Date(), 1);
          return { from: yesterday, to: yesterday };
        }
      },
    ]
  },
  {
    label: "Days",
    presets: [
      {
        label: "Last 7 days",
        getValue: () => ({ from: subDays(new Date(), 6), to: new Date() })
      },
      {
        label: "Last 30 days",
        getValue: () => ({ from: subDays(new Date(), 29), to: new Date() })
      },
      {
        label: "Last 90 days",
        getValue: () => ({ from: subDays(new Date(), 89), to: new Date() })
      },
    ]
  },
  {
    label: "Weeks",
    presets: [
      { 
        label: "This week", 
        getValue: () => {
          const today = new Date();
          const dayOfWeek = today.getDay();
          const monday = subDays(today, dayOfWeek === 0 ? 6 : dayOfWeek - 1);
          return { from: monday, to: today };
        }
      },
      { 
        label: "Last week", 
        getValue: () => {
          const today = new Date();
          const dayOfWeek = today.getDay();
          const thisSunday = subDays(today, dayOfWeek === 0 ? 0 : dayOfWeek);
          const lastSunday = subDays(thisSunday, 7);
          const lastMonday = subDays(lastSunday, 6);
          return { from: lastMonday, to: lastSunday };
        }
      },
    ]
  },
  {
    label: "Months",
    presets: [
      { 
        label: "This month", 
        getValue: () => ({ from: startOfMonth(new Date()), to: new Date() })
      },
      { 
        label: "Last month", 
        getValue: () => {
          const lastMonth = subMonths(new Date(), 1);
          return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
        }
      },
    ]
  },
  {
    label: "Years",
    presets: [
      { 
        label: "This year", 
        getValue: () => ({ from: startOfYear(new Date()), to: new Date() })
      },
      { 
        label: "Last year", 
        getValue: () => {
          const lastYear = subYears(new Date(), 1);
          return { from: startOfYear(lastYear), to: endOfYear(lastYear) };
        }
      },
      {
        label: "Last 12 months",
        getValue: () => ({ from: subYears(new Date(), 1), to: new Date() })
      }
    ]
  }
];

interface DateDropdownProps {
  period: string;
  onDateChange: (period: string) => void;
  triggerLabel?: string;
  customOnly?: boolean;
  /** Controlled open state (e.g. header Custom chip opens the picker) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Extra classes on the trigger (e.g. hide on small screens while keeping an anchor) */
  triggerClassName?: string;
}

export default function DateDropdown({
  period,
  onDateChange,
  triggerLabel,
  customOnly = false,
  open: controlledOpen,
  onOpenChange,
  triggerClassName,
}: DateDropdownProps) {
  const isMobile = useIsMobile();
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>();
  const [isCustomRange, setIsCustomRange] = useState(period.includes(":"));
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  // Only highlight a sidebar preset when the user clicked it — not when calendar
  // dates happen to equal that preset's span (e.g. ~30-day custom pick).
  const [activePresetLabel, setActivePresetLabel] = useState<string | null>(null);

  const isControlled = controlledOpen !== undefined;
  const popoverOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const setPopoverOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  
  // When customOnly is true and popover opens, show calendar
  useEffect(() => {
    if (customOnly && popoverOpen) {
      setShowCalendar(true);
    } else if (!popoverOpen) {
      setShowCalendar(false);
      setActivePresetLabel(null);
    }
  }, [customOnly, popoverOpen]);

  // Sync local state from URL period (custom absolute range or named preset)
  useEffect(() => {
    const custom = period.includes(":");
    setIsCustomRange(custom);
    setActivePresetLabel(null);

    if (custom) {
      const [from, to] = period.split(":");
      try {
        const fromDate = parse(from, "yyyy-MM-dd", new Date());
        const toDate = parse(to, "yyyy-MM-dd", new Date());
        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          setDateRange({ from: fromDate, to: toDate });
          setTempDateRange({ from: fromDate, to: toDate });
        }
      } catch {
        // Invalid date format
      }
      return;
    }

    // Named preset — clear absolute range so the calendar starts fresh
    setDateRange(undefined);
    setTempDateRange(undefined);
  }, [period]);

  const currentOption = periodOptions.find((opt) => opt.value === period);

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setTempDateRange(range);
    // Manual calendar pick is always Custom — do not inherit a preset highlight
    setActivePresetLabel(null);
  };

  const handleApplyDateRange = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setDateRange(tempDateRange);
      setIsCustomRange(true);
      // Format as YYYY-MM-DD:YYYY-MM-DD — keep absolute custom, never remap to L7/30d
      const customPeriod = `${format(
        tempDateRange.from,
        "yyyy-MM-dd"
      )}:${format(tempDateRange.to, "yyyy-MM-dd")}`;
      onDateChange(customPeriod);
      setShowCalendar(false);
      setPopoverOpen(false);
    }
  };

  const handleQuickSelect = (range: DateRange, label: string) => {
    setTempDateRange(range);
    setActivePresetLabel(label);
  };

  const handleCancel = () => {
    setTempDateRange(dateRange);
    setActivePresetLabel(null);
    setPopoverOpen(false);
  };

  const handlePresetSelect = (value: string) => {
    setIsCustomRange(false);
    setActivePresetLabel(null);
    onDateChange(value);
    setPopoverOpen(false);
  };

  const getDisplayLabel = () => {
    // Check if period is custom range format (YYYY-MM-DD:YYYY-MM-DD)
    if (period.includes(":")) {
      const [from, to] = period.split(":");
      try {
        const fromDate = parse(from, "yyyy-MM-dd", new Date());
        const toDate = parse(to, "yyyy-MM-dd", new Date());
        const currentYear = new Date().getFullYear();
        
        const showYear = fromDate.getFullYear() !== currentYear || toDate.getFullYear() !== currentYear || fromDate.getFullYear() !== toDate.getFullYear();
        const formatStr = showYear ? "MMM d, yy" : "MMM d";
        
        return `${format(fromDate, formatStr)} - ${format(toDate, formatStr)}`;
      } catch {
        return "Custom Range";
      }
    }
    return currentOption?.label || "Last 7 days";
  };

  const shouldShowCalendar = customOnly || showCalendar;

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-custom-range={isCustomRange || undefined}
          className={cn(shellControlClass, "shrink-0", triggerClassName)}
        >
          <HugeiconsIcon icon={Calendar01Icon} size={13} />
          <span className="hidden sm:inline whitespace-nowrap">{triggerLabel || getDisplayLabel()}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end" 
        sideOffset={8}
        className={cn(
          "p-0 overflow-hidden rounded-xl border-border",
          shouldShowCalendar ? "w-auto max-w-[100vw]" : "w-56"
        )}
      >
        {!shouldShowCalendar ? (
          <div className="py-2">
            {/* Preset Options */}
            <div className="px-2">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePresetSelect(option.value)}
                  className={cn(
                    "w-full px-3 py-2.5 text-left text-[13px] rounded-md transition-all duration-200",
                    option.value === period && !isCustomRange
                      ? "bg-muted text-foreground font-semibold"
                      : "text-foreground/80 hover:bg-muted/50 hover:text-foreground dark:hover:bg-muted/40"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            <div className="h-px bg-border/30 my-2 mx-4" />
            
            {/* Custom Range */}
            <div className="px-2">
              <button
                onClick={() => setShowCalendar(true)}
                className={cn(
                  "w-full px-3 py-2.5 text-left text-[13px] rounded-md transition-all duration-200",
                  isCustomRange
                    ? "bg-muted text-foreground font-semibold"
                    : "text-foreground/80 hover:bg-muted/50 hover:text-foreground dark:hover:bg-muted/40"
                )}
              >
                Custom range...
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col-reverse md:flex-row">
            {/* Presets rail: same row pattern as the sidebar (selected = white row, hairline ring) */}
            <div className="flex gap-1 overflow-x-auto border-t border-border bg-canvas p-2 md:w-[164px] md:flex-col md:gap-0 md:overflow-visible md:border-r md:border-t-0">
              {presetGroups.map((group, groupIndex) => (
                <div
                  key={group.label}
                  role="group"
                  aria-label={group.label}
                  className={cn("flex gap-1 md:flex-col md:gap-0.5", groupIndex > 0 && "md:mt-1.5 md:border-t md:border-border md:pt-1.5")}
                >
                  {group.presets.map((preset) => {
                    const presetValue = preset.getValue();
                    // Highlight only when the user chose this preset AND the
                    // temp range still matches its computed dates.
                    const datesMatch =
                      !!tempDateRange?.from &&
                      !!tempDateRange?.to &&
                      format(tempDateRange.from, "yyyy-MM-dd") === format(presetValue.from, "yyyy-MM-dd") &&
                      format(tempDateRange.to, "yyyy-MM-dd") === format(presetValue.to, "yyyy-MM-dd");
                    const isSelected = activePresetLabel === preset.label && datesMatch;
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => handleQuickSelect(presetValue, preset.label)}
                        className={cn(
                          "h-7 shrink-0 whitespace-nowrap rounded-lg px-2.5 text-left text-[13px] leading-4 transition-colors",
                          isSelected
                            ? "bg-card font-medium text-foreground shadow-[0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]"
                            : "text-foreground/70 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
                        )}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex flex-col">
              <Calendar
                mode="range"
                selected={tempDateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={isMobile ? 1 : 2}
                defaultMonth={tempDateRange?.from ?? subMonths(new Date(), isMobile ? 0 : 1)}
                showOutsideDays={false}
                className="bg-transparent p-4"
                disabled={(date) => date > new Date()}
              />

              <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  {!customOnly && (
                    <button
                      type="button"
                      onClick={() => setShowCalendar(false)}
                      aria-label="Back to presets"
                      className="-ml-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-foreground/60 transition-colors hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
                    >
                      <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
                    </button>
                  )}
                  <p className="truncate text-[13px] leading-4 text-foreground tabular-nums" aria-live="polite">
                    {tempDateRange?.from ? (
                      <>
                        {format(tempDateRange.from, "MMM d, yyyy")}
                        {tempDateRange.to ? ` – ${format(tempDateRange.to, "MMM d, yyyy")}` : " – pick an end date"}
                        {tempDateRange.to && (
                          <span className="ml-2 text-muted-foreground">
                            {differenceInCalendarDays(tempDateRange.to, tempDateRange.from) + 1} days
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">Pick a start date</span>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button type="button" onClick={handleCancel} className={shellControlClass}>
                    Cancel
                  </button>
                  <Button
                    size="sm"
                    onClick={handleApplyDateRange}
                    disabled={!tempDateRange?.from || !tempDateRange?.to}
                    className="h-[34px] rounded-lg px-3.5 text-[12px] font-medium"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
